import { backgroundOrange, darkOrange, gold, green, orange } from '@/theme/colors'
import styled from 'styled-components'

export const DetailPageContainer = styled.div`
    background-color: ${backgroundOrange};
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`

export const LoadingContainer = styled.div`
    display: flex;
    flex: 1;
    justify-content: center;
    align-items: center;
    background-color: ${backgroundOrange};
    height: 100%;
`

export const ErrorContainer = styled.div`
    padding: 2rem;
    text-align: center;
    color: red;
`

export const NotFoundMessage = styled.div`
    padding: 2rem;
    text-align: center;
    color: #6b7280;
`

export const ScrollContainer = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 1.5rem;
`

export const DetailCard = styled.div`
    background: white;
    border-radius: 16px;
    border: 1px solid #f0e0d6;
    max-width: 720px;
    margin: 0 auto;
    overflow: hidden;
`

export const DetailHeader = styled.div`
    padding: 1.5rem 1.5rem 0;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
`

export const DetailTitle = styled.h1`
    margin: 0;
    font-size: 1.5rem;
    font-weight: 700;
    color: #262626;
    line-height: 1.3;
    word-break: break-word;
`

export const ActionButtons = styled.div`
    display: flex;
    gap: 0.5rem;
    flex-shrink: 0;
`

export const IconButton = styled.button<{ $variant?: 'default' | 'danger' }>`
    background: none;
    border: 1px solid ${({ $variant }) => ($variant === 'danger' ? '#fca5a5' : '#e5e7eb')};
    border-radius: 8px;
    padding: 0.5rem;
    cursor: pointer;
    color: ${({ $variant }) => ($variant === 'danger' ? '#dc2626' : '#6b7280')};
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s;

    &:hover {
        border-color: ${({ $variant }) => ($variant === 'danger' ? '#dc2626' : orange)};
        color: ${({ $variant }) => ($variant === 'danger' ? '#dc2626' : orange)};
        background: ${({ $variant }) => ($variant === 'danger' ? '#fef2f2' : '#fef3e2')};
    }
`

export const DetailBody = styled.div`
    padding: 1.25rem 1.5rem 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
`

/* ---- Location Section ---- */

export const LocationSection = styled.div`
    display: flex;
    align-items: center;
    gap: 0.625rem;
`

export const LocationIcon = styled.div`
    color: ${orange};
    flex-shrink: 0;
    display: flex;
`

export const LocationText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
    flex: 1;
`

export const LocationPrimary = styled.span`
    font-size: 0.9375rem;
    font-weight: 500;
    color: #262626;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

export const LocationSecondary = styled.span`
    font-size: 0.75rem;
    color: #6b7280;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

export const MapButton = styled.a`
    font-size: 0.75rem;
    font-weight: 600;
    color: ${orange};
    text-decoration: none;
    padding: 0.375rem 0.75rem;
    border: 1px solid ${orange}40;
    border-radius: 6px;
    flex-shrink: 0;
    transition: all 0.15s;
    white-space: nowrap;

    &:hover {
        background: #fef3e2;
        border-color: ${orange};
    }
`

/* ---- Dates Section ---- */

export const DatesSection = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    padding: 1rem;
    background: ${backgroundOrange};
    border-radius: 12px;
`

export const DateBlock = styled.div<{ $align?: 'left' | 'right' }>`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    align-items: ${({ $align }) => ($align === 'right' ? 'flex-end' : 'flex-start')};
    text-align: ${({ $align }) => ($align === 'right' ? 'right' : 'left')};
`

export const DateBlockLabel = styled.span`
    font-size: 0.6875rem;
    font-weight: 700;
    color: ${gold};
    text-transform: uppercase;
    letter-spacing: 0.04em;
`

export const DateBlockValue = styled.span`
    font-size: 0.9375rem;
    font-weight: 600;
    color: #262626;
`

export const DateBlockTime = styled.span`
    font-size: 0.8125rem;
    color: #6b7280;
`

/* ---- Guests Section ---- */

export const SectionLabel = styled.h3`
    font-size: 0.8125rem;
    font-weight: 700;
    color: ${darkOrange};
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin: 0 0 0.75rem;
`

export const GuestList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`

export const GuestItem = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem 0.75rem;
    background: #fafafa;
    border-radius: 8px;
`

export const GuestAvatar = styled.div`
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: ${orange};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8125rem;
    font-weight: 600;
    flex-shrink: 0;
    overflow: hidden;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`

export const GuestName = styled.span`
    font-size: 0.875rem;
    font-weight: 500;
    color: #262626;
    flex: 1;
`

export const YouPill = styled.span`
    font-size: 0.6875rem;
    font-weight: 600;
    color: ${green};
    background: #e8f5e9;
    padding: 0.125rem 0.5rem;
    border-radius: 999px;
`

/* ---- Links Section ---- */

export const LinksList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`

export const LinkItem = styled.a`
    font-size: 0.875rem;
    color: ${orange};
    text-decoration: none;
    padding: 0.625rem 0.75rem;
    background: #fafafa;
    border-radius: 8px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: block;
    transition: background 0.15s;

    &:hover {
        background: #fef3e2;
        text-decoration: underline;
    }
`

/* ---- Attachments Section ---- */

export const AttachmentButton = styled.button`
    font-size: 0.875rem;
    color: ${orange};
    font-weight: 500;
    padding: 0.625rem 0.75rem;
    background: #fafafa;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-family: inherit;
    text-align: left;
    width: 100%;
    transition: background 0.15s;

    &:hover {
        background: #fef3e2;
    }
`

/* ---- Notes Section ---- */

export const NotesContent = styled.div`
    font-size: 0.875rem;
    color: #374151;
    line-height: 1.6;
    white-space: pre-wrap;
    word-break: break-word;
    padding: 0.75rem;
    background: #fafafa;
    border-radius: 8px;
`

/* ---- Metadata ---- */

export const MetaSection = styled.div`
    font-size: 0.6875rem;
    color: #9ca3af;
    border-top: 1px solid #f0e0d6;
    padding-top: 1rem;
    display: flex;
    gap: 1.5rem;
`
